import React from 'react';
import {connect} from 'react-redux';
import util from 'Utils/commonUtil';
import BaseImageUploader from 'Components/common/BaseImageUploader';
import BasicButton from 'Components/common/BasicButton';
import {Person, PersonAddOutlined} from '@material-ui/icons';
import {RECEIVE_METHOD} from 'Utils/constant';
import {openNotification} from 'Actions/notification';
import {
    deleteShipmentAttachment,
    getReceiveCode,
    receiveOrder,
    updateShipment,
    uploadReceiveImage,
} from 'Actions/host';
import {T} from 'Actions/translate';

const MAX_OTHER_IMAGE_COUNT = 10;

class ShipmentReceiveWithOtherPhotosDialog extends React.Component {
    mainImageInputRef = React.createRef();
    otherImageInputRef = React.createRef();
    submitPending = false;
    otherImagesPreviewPending = false;

    state = {
        receiveMethod: RECEIVE_METHOD.CUSTOMER,
        verificationCode: '',
        codeSent: false,
        codeCountdown: 0,
        hasSentCode: false,
        chooseImage: {file: null, url: ''},
        otherImages: [],
        selectedOtherImageId: '',
        otherUploading: false,
    };

    codeCountdownTimer = null;

    componentDidUpdate(prevProps) {
        if (!prevProps.opened && this.props.opened) {
            this.resetState(this.props);
        }
        if (prevProps.maskedPhone !== this.props.maskedPhone) {
            this.clearCodeCountdown();
            this.setState({
                codeSent: false,
                codeCountdown: 0,
                hasSentCode: false,
            });
        }
    }

    componentWillUnmount() {
        this.clearCodeCountdown();
        this.submitPending = false;
        this.otherImagesPreviewPending = false;
    }

    createMainImageState = (props = this.props) => {
        const initialMainImageUrl =
            props.initialMainImageUrl || props.initialMainImageKey || '';
        return {
            file: null,
            url: initialMainImageUrl,
            persistedUrl: props.initialMainImageKey || '',
        };
    };

    createOtherImagesState = (props = this.props) => {
        const initialOtherImages = props.initialOtherImages || [];
        return initialOtherImages.map((otherImage, index) => ({
            id: otherImage.id || `${Date.now()}_${index}_${Math.random()}`,
            file: null,
            url: otherImage.imageUrl || otherImage.url || '',
            persistedUrl:
                otherImage.fileKey ||
                otherImage.imageUrl ||
                otherImage.url ||
                '',
            status: 'success',
        }));
    };

    buildInitialState = (props = this.props) => {
        const otherImages = this.createOtherImagesState(props);
        return {
            receiveMethod:
                props.initialReceiveMethod || RECEIVE_METHOD.CUSTOMER,
            verificationCode: props.initialVerificationCode || '',
            codeSent: false,
            codeCountdown: 0,
            hasSentCode: false,
            chooseImage: this.createMainImageState(props),
            otherImages,
            selectedOtherImageId: otherImages[0] ? otherImages[0].id : '',
        };
    };

    resetState = (props = this.props) => {
        this.clearCodeCountdown();
        this.clearInputValue(this.mainImageInputRef);
        this.clearInputValue(this.otherImageInputRef);
        this.submitPending = false;
        this.otherImagesPreviewPending = false;
        this.setState(this.buildInitialState(props));
    };

    clearInputValue = (inputRef) => {
        if (inputRef && inputRef.current) {
            inputRef.current.value = '';
        }
    };

    maskPhoneNumber = (phone) => {
        if (!phone) {
            return '';
        }
        if (phone.includes('*')) {
            return phone;
        }
        const digits = phone.replace(/\D/g, '');
        if (digits.length < 7) {
            return phone;
        }
        return `${digits.slice(0, 3)}****${digits.slice(-4)}`;
    };

    startCodeCountdown = () => {
        this.clearCodeCountdown();
        this.setState(
            {
                codeSent: true,
                codeCountdown: 30,
                hasSentCode: true,
            },
            () => {
                this.codeCountdownTimer = setInterval(() => {
                    this.setState((prevState) => {
                        if (prevState.codeCountdown <= 1) {
                            this.clearCodeCountdown();
                            return {codeCountdown: 0, codeSent: false};
                        }
                        return {codeCountdown: prevState.codeCountdown - 1};
                    });
                }, 1000);
            },
        );
    };

    clearCodeCountdown = () => {
        if (this.codeCountdownTimer) {
            clearInterval(this.codeCountdownTimer);
            this.codeCountdownTimer = null;
        }
    };

    isInteractionLocked = () => {
        const {uploadFileBtnIsLoading} = this.props;
        return (
            uploadFileBtnIsLoading ||
            this.submitPending ||
            this.state.otherUploading
        );
    };

    handleMethodChange = (receiveMethod) => {
        if (this.isInteractionLocked()) {
            return;
        }
        if (this.state.receiveMethod === receiveMethod) {
            return;
        }
        this.clearCodeCountdown();
        this.clearInputValue(this.mainImageInputRef);
        this.clearInputValue(this.otherImageInputRef);
        this.submitPending = false;
        this.setState(
            this.buildInitialState({
                ...this.props,
                initialReceiveMethod: receiveMethod,
                initialVerificationCode: '',
                initialMainImageUrl: '',
                initialMainImageKey: '',
                initialOtherImages: [],
            }),
        );
    };

    handleVerificationCodeChange = (event) => {
        if (this.isInteractionLocked()) {
            return;
        }
        this.setState({verificationCode: event.target.value});
    };

    handleChooseMainImage = () => {
        if (this.isInteractionLocked()) {
            return;
        }
        if (this.mainImageInputRef.current) {
            this.mainImageInputRef.current.click();
        }
    };

    handleChooseOtherImages = () => {
        if (this.isInteractionLocked() || this.otherImagesPreviewPending) {
            return;
        }
        if (this.state.otherImages.length >= MAX_OTHER_IMAGE_COUNT) {
            this.props.openNotification(
                T('shippment_upload_other_photos_limit', [
                    MAX_OTHER_IMAGE_COUNT,
                ]),
                'danger',
            );
            return;
        }
        if (this.otherImageInputRef.current) {
            this.otherImageInputRef.current.click();
        }
    };

    readFilePreview = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => resolve(event.target.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });

    handleSelectedMainImage = async (event) => {
        if (this.isInteractionLocked()) {
            this.clearInputValue(this.mainImageInputRef);
            return;
        }
        const file =
            event.target && event.target.files && event.target.files[0];
        if (!file) {
            return;
        }
        if (!/^image\//.test(file.type)) {
            this.props.openNotification(
                T('notification_please_select_picture'),
                'danger',
            );
            this.clearInputValue(this.mainImageInputRef);
            return;
        }
        const url = await this.readFilePreview(file);
        this.setState({chooseImage: {file, url, persistedUrl: ''}});
    };

    handleSelectedOtherImages = async (event) => {
        if (this.isInteractionLocked() || this.otherImagesPreviewPending) {
            this.clearInputValue(this.otherImageInputRef);
            return;
        }
        const selectedFiles = Array.from(
            (event.target && event.target.files) || [],
        );
        if (!selectedFiles.length) {
            return;
        }
        const restCount = MAX_OTHER_IMAGE_COUNT - this.state.otherImages.length;
        const availableFiles = selectedFiles.slice(0, restCount);
        const invalidFile = availableFiles.find(
            (file) => !/^image\//.test(file.type),
        );
        if (invalidFile) {
            this.props.openNotification(
                T('notification_please_select_picture'),
                'danger',
            );
            this.clearInputValue(this.otherImageInputRef);
            return;
        }
        this.otherImagesPreviewPending = true;
        let appendedImages = [];
        try {
            const previewUrls = await Promise.all(
                availableFiles.map((file) => this.readFilePreview(file)),
            );
            appendedImages = previewUrls.map((url, index) => ({
                id: `${Date.now()}_${index}_${Math.random()}`,
                file: availableFiles[index],
                url,
                persistedUrl: '',
                status: 'uploading',
            }));
            let hasExceededLimit = selectedFiles.length > restCount;
            this.setState((prevState) => {
                const nextRestCount =
                    MAX_OTHER_IMAGE_COUNT - prevState.otherImages.length;
                const safeAppendedImages = appendedImages.slice(
                    0,
                    Math.max(nextRestCount, 0),
                );
                const nextOtherImages =
                    prevState.otherImages.concat(safeAppendedImages);

                if (safeAppendedImages.length < appendedImages.length) {
                    hasExceededLimit = true;
                    appendedImages = safeAppendedImages;
                }

                return {
                    otherImages: nextOtherImages,
                    selectedOtherImageId:
                        prevState.selectedOtherImageId ||
                        (safeAppendedImages[0] ? safeAppendedImages[0].id : ''),
                    otherUploading: true,
                };
            });
            if (hasExceededLimit) {
                this.props.openNotification(
                    T('shippment_upload_other_photos_limit', [
                        MAX_OTHER_IMAGE_COUNT,
                    ]),
                    'danger',
                );
            }
        } finally {
            this.otherImagesPreviewPending = false;
            this.clearInputValue(this.otherImageInputRef);
        }

        // Upload each new image immediately after preview is shown
        const {receiveMethod} = this.state;
        const uploadType = this.getOtherUploadType(receiveMethod);
        for (const img of appendedImages) {
            try {
                const response = await this.props.uploadReceiveFile({
                    type: uploadType,
                    file: img.file,
                });
                this.setState((prevState) => ({
                    otherImages: prevState.otherImages.map((o) =>
                        o.id === img.id
                            ? {
                                  ...o,
                                  persistedUrl: response.url,
                                  file: null,
                                  status: 'success',
                              }
                            : o,
                    ),
                }));
            } catch (err) {
                this.updateOtherImageStatus(img.id, 'error');
            }
        }
        this.setState({otherUploading: false});
    };

    getRelativeKey = (url) => {
        if (!url) return '';
        try {
            const pathname = new URL(url).pathname;
            return pathname.startsWith('/') ? pathname.slice(1) : pathname;
        } catch (_) {
            return url;
        }
    };

    getPersistedOtherImageFileKey = (otherImage) => {
        const persistedUrl = otherImage && otherImage.persistedUrl;
        if (!persistedUrl || typeof persistedUrl !== 'string') {
            return '';
        }

        const normalizedUrl = persistedUrl.trim();
        if (
            !normalizedUrl ||
            /^data:/i.test(normalizedUrl) ||
            /^blob:/i.test(normalizedUrl)
        ) {
            return '';
        }

        return this.getRelativeKey(normalizedUrl);
    };

    removeOtherImageFromState = (imageId) => {
        this.setState((prevState) => {
            const nextOtherImages = prevState.otherImages.filter(
                (otherImage) => otherImage.id !== imageId,
            );
            return {
                otherImages: nextOtherImages,
                selectedOtherImageId:
                    prevState.selectedOtherImageId === imageId
                        ? nextOtherImages[0]
                            ? nextOtherImages[0].id
                            : ''
                        : prevState.selectedOtherImageId,
            };
        });
    };

    isDeleteAttachmentBlankError = (err) => {
        const message = `${(err && err.message) || ''} ${
            (err && err.status) || ''
        }`.toLowerCase();

        return (
            message.indexOf('modeid or filekey is blank') > -1 ||
            (message.indexOf('modeid') > -1 &&
                message.indexOf('filekey') > -1 &&
                message.indexOf('blank') > -1)
        );
    };

    handleRemoveOtherImage = async (imageId) => {
        if (this.isInteractionLocked()) {
            return;
        }

        const otherImage = this.state.otherImages.find(
            (image) => image.id === imageId,
        );
        if (!otherImage) {
            return;
        }

        const fileKey = this.getPersistedOtherImageFileKey(otherImage);
        if (!fileKey) {
            this.removeOtherImageFromState(imageId);
            return;
        }

        try {
            await this.props.deleteShipmentAttachment({
                modeId: this.props.shipmentId,
                fileKey,
            });
            this.removeOtherImageFromState(imageId);
        } catch (err) {
            if (this.isDeleteAttachmentBlankError(err)) {
                this.props.openNotification(
                    'modeId或fileKey不能为空 / modeId or fileKey is blank',
                    'danger',
                );
                return;
            }
            this.props.openNotification(
                '删除图片失败 / Failed to delete image',
                'danger',
            );
        }
    };

    handleSelectOtherImage = (selectedOtherImageId) => {
        if (this.isInteractionLocked()) {
            return;
        }
        this.setState({selectedOtherImageId});
    };

    updateOtherImageStatus = (imageId, status) => {
        this.setState((prevState) => ({
            otherImages: prevState.otherImages.map((otherImage) =>
                otherImage.id === imageId
                    ? {...otherImage, status}
                    : otherImage,
            ),
        }));
    };

    sendDeliveryCode = () => {
        if (this.isInteractionLocked()) {
            return;
        }
        const {maskedPhone, shipmentId} = this.props;
        if (this.state.receiveMethod !== RECEIVE_METHOD.DELEGATE) {
            return;
        }
        this.props
            .getReceiveCode({
                shipmentId,
                tel: maskedPhone,
            })
            .then((res) => {
                if (res && res.status === 'success') {
                    this.startCodeCountdown();
                    this.props.openNotification(
                        T('shippment_upload_send_code_success'),
                        'success',
                    );
                } else if (res && res.status === 'invalid_phone_number ') {
                    this.props.openNotification(
                        T('shippment_upload_send_code_tel_incorrect'),
                        'error',
                    );
                } else if (res && res.status === 'invalid_status ') {
                    this.props.openNotification(
                        T('shippment_upload_send_code_status_incorrect'),
                        'error',
                    );
                }
            });
    };

    getMainUploadType = (receiveMethod) =>
        receiveMethod === RECEIVE_METHOD.DELEGATE
            ? 'location_image'
            : 'receipt';

    getOtherUploadType = (receiveMethod) =>
        receiveMethod === RECEIVE_METHOD.DELEGATE
            ? 'location_other_image'
            : 'receipt_other_image';

    isSubmitButtonDisabled = () => {
        const {mode} = this.props;
        const {chooseImage, verificationCode, receiveMethod} = this.state;
        if (this.isInteractionLocked()) {
            return true;
        }
        if (!chooseImage.file && !chooseImage.url) {
            return true;
        }
        if (
            mode !== 'update' &&
            receiveMethod === RECEIVE_METHOD.DELEGATE &&
            !verificationCode.trim()
        ) {
            return true;
        }
        return false;
    };

    handleSubmit = async () => {
        const {
            shipmentId,
            receiveOrderRequest,
            updateShipmentRequest,
            uploadReceiveFile,
            onSuccess,
            onLoadingChange,
            uploadFileBtnIsLoading,
            mode,
        } = this.props;
        const {chooseImage, otherImages, receiveMethod, verificationCode} =
            this.state;

        if (
            this.isSubmitButtonDisabled() ||
            this.submitPending ||
            uploadFileBtnIsLoading
        ) {
            return;
        }

        this.submitPending = true;
        onLoadingChange(true);
        try {
            let mainImageUrl = chooseImage.persistedUrl || chooseImage.url;
            if (chooseImage.file) {
                const mainImageResponse = await uploadReceiveFile({
                    type: this.getMainUploadType(receiveMethod),
                    file: chooseImage.file,
                });
                mainImageUrl = mainImageResponse.url;
            }
            const otherImageUrls = otherImages
                .filter(
                    (otherImage) =>
                        otherImage.status === 'success' &&
                        (otherImage.persistedUrl || otherImage.url),
                )
                .map((otherImage) => otherImage.persistedUrl || otherImage.url);
            const payload = {
                shipmentId,
                receiveImg:
                    receiveMethod === RECEIVE_METHOD.CUSTOMER
                        ? mainImageUrl
                        : undefined,
                receiveType: receiveMethod === RECEIVE_METHOD.CUSTOMER ? 1 : 2,
                receivingCode:
                    mode === 'update'
                        ? null
                        : verificationCode
                        ? verificationCode.trim()
                        : null,
                locationImg:
                    receiveMethod === RECEIVE_METHOD.DELEGATE
                        ? mainImageUrl
                        : undefined,
                otherImageUrls,
            };
            if (mode === 'update') {
                await updateShipmentRequest(payload);
            } else {
                await receiveOrderRequest(payload);
            }
            if (typeof onSuccess === 'function') {
                onSuccess();
            }
        } finally {
            this.submitPending = false;
            onLoadingChange(false);
        }
    };

    renderOtherPhotoContent() {
        const {otherImages, selectedOtherImageId} = this.state;
        const interactionLocked = this.isInteractionLocked();
        if (!otherImages.length) {
            return (
                <BaseImageUploader
                    className="other-photo-upload-empty"
                    disabled={interactionLocked}
                    tag="button"
                    type="button"
                    isPreventDefault={false}
                    onClickOfSingleTap={this.handleChooseOtherImages}>
                    <span className="other-photo-upload-plus">+</span>
                    <span className="other-photo-upload-empty-text">
                        {T('shippment_upload_other_photos_here')}
                    </span>
                </BaseImageUploader>
            );
        }

        const selectedOtherImage =
            otherImages.find(
                (otherImage) => otherImage.id === selectedOtherImageId,
            ) || otherImages[0];

        return (
            <div className="other-photo-upload-filled">
                <div className="other-photo-upload-count">
                    {T('shippment_upload_other_photos_selected', [
                        otherImages.length,
                        MAX_OTHER_IMAGE_COUNT,
                    ])}
                </div>
                <div className="other-photo-upload-preview">
                    <img src={selectedOtherImage.url} alt="" />
                </div>
                <div className="other-photo-upload-grid">
                    {otherImages.map((otherImage) => (
                        <div
                            className={util.getClassName({
                                'other-photo-upload-item': true,
                                'is-active':
                                    selectedOtherImage.id === otherImage.id,
                                'is-disabled': interactionLocked,
                            })}
                            key={otherImage.id}
                            onClick={() =>
                                this.handleSelectOtherImage(otherImage.id)
                            }>
                            <img src={otherImage.url} alt="" />
                            <button
                                type="button"
                                className="other-photo-upload-remove"
                                disabled={interactionLocked}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    this.handleRemoveOtherImage(otherImage.id);
                                }}>
                                ×
                            </button>
                            {otherImage.status === 'uploading' ? (
                                <span className="other-photo-upload-status">
                                    {T('loading')}
                                </span>
                            ) : null}
                            {otherImage.status === 'error' ? (
                                <span className="other-photo-upload-status is-error">
                                    {T('shippment_upload_other_photos_failed')}
                                </span>
                            ) : null}
                        </div>
                    ))}
                    {otherImages.length < MAX_OTHER_IMAGE_COUNT ? (
                        <button
                            type="button"
                            className="other-photo-upload-add"
                            disabled={interactionLocked}
                            onClick={this.handleChooseOtherImages}>
                            <span className="other-photo-upload-add-plus">
                                +
                            </span>
                            <span className="other-photo-upload-add-text">
                                {T('shippment_upload_other_photos_add')}
                            </span>
                        </button>
                    ) : null}
                </div>
            </div>
        );
    }

    render() {
        const {
            maskedPhone,
            shipment,
            uploadFileBtnIsLoading,
            onClose,
            mode,
            lockReceiveMethod,
        } = this.props;
        const {
            chooseImage,
            receiveMethod,
            verificationCode,
            codeSent,
            codeCountdown,
            hasSentCode,
        } = this.state;
        const interactionLocked = this.isInteractionLocked();
        const isUpdateMode = mode === 'update';
        const emailDisplay =
            (shipment &&
                (shipment.invoiceReceiverEmail || shipment.email || '')) ||
            '';
        const missingContact = !maskedPhone && !emailDisplay;
        const sendCodeDisabled =
            interactionLocked || missingContact || codeCountdown > 0;
        let sendCodeLabelKey =
            'shippment_upload_signature_method_sent_delivery_code';
        if (codeSent && codeCountdown > 0) {
            sendCodeLabelKey = 'shippment_upload_signature_method_code_sent';
        } else if (hasSentCode) {
            sendCodeLabelKey =
                'shippment_upload_signature_method_code_not_resent';
        }
        let sendCodeLabel = T(sendCodeLabelKey);
        if (codeSent && codeCountdown > 0) {
            sendCodeLabel = `${sendCodeLabel} (${codeCountdown}s)`;
        }

        return (
            <div className="uploa-image-dialog other-photo-receive-dialog">
                <h5 className="upload-method-title text-center">
                    {T('shippment_upload_signature_method_title')}
                </h5>
                <p className="upload-method-subtitle text-center">
                    {receiveMethod === RECEIVE_METHOD.CUSTOMER
                        ? T('shippment_upload_sign_receipt_by_photo')
                        : T('shippment_upload_signature_send_delivery_code')}
                </p>

                <div className="signature-method-section">
                    <div className="signature-method-section-header">
                        <span className="signature-method-section-text">
                            {T('shippment_upload_signature_method_select')}
                        </span>
                    </div>
                    <div className="signature-method-option-list">
                        <button
                            type="button"
                            className={util.getClassName({
                                'signature-method-option': true,
                                'is-active':
                                    receiveMethod === RECEIVE_METHOD.CUSTOMER,
                            })}
                            testid="receipt-method-customer-radio"
                            disabled={interactionLocked || lockReceiveMethod}
                            onClick={() =>
                                this.handleMethodChange(RECEIVE_METHOD.CUSTOMER)
                            }>
                            <Person />
                            <span className="signature-method-option-text">
                                {T(
                                    'shippment_upload_signature_method_customer',
                                )}
                            </span>
                            <span className="signature-method-option-badge" />
                        </button>
                        <button
                            type="button"
                            className={util.getClassName({
                                'signature-method-option': true,
                                'is-active':
                                    receiveMethod === RECEIVE_METHOD.DELEGATE,
                            })}
                            testid="receipt-method-delegate-radio"
                            disabled={interactionLocked || lockReceiveMethod}
                            onClick={() =>
                                this.handleMethodChange(RECEIVE_METHOD.DELEGATE)
                            }>
                            <PersonAddOutlined />
                            <span className="signature-method-option-text">
                                {T(
                                    'shippment_upload_signature_method_delegate',
                                )}
                            </span>
                            <span className="signature-method-option-badge" />
                        </button>
                    </div>
                    {receiveMethod === RECEIVE_METHOD.DELEGATE &&
                    !isUpdateMode ? (
                        <div className="signature-method-info">
                            <div className="signature-method-info-card">
                                <div className="signature-method-info-sendto">
                                    {T(
                                        'shippment_upload_signature_method_sentto_code',
                                    )}
                                </div>
                                <div className="signature-method-info-row">
                                    <span className="signature-method-info-label">
                                        {T(
                                            'shippment_upload_signature_method_tel',
                                        )}
                                    </span>
                                    <span className="signature-method-info-value signature-method-info-tel">
                                        {this.maskPhoneNumber(maskedPhone)}
                                    </span>
                                </div>
                                <div className="signature-method-info-row">
                                    <span className="signature-method-info-label">
                                        {T(
                                            'shippment_upload_signature_method_email',
                                        )}
                                    </span>
                                    <span className="signature-method-info-value signature-method-info-email">
                                        {emailDisplay}
                                    </span>
                                </div>
                                <div className="signature-method-info-hint">
                                    {T(
                                        'shippment_upload_signature_method_send_info',
                                    )}
                                </div>
                            </div>
                            <BasicButton
                                type="primary"
                                className={util.getClassName({
                                    'signature-method-info-send-btn': true,
                                    disabled: sendCodeDisabled,
                                })}
                                disabled={sendCodeDisabled}
                                onClickOfSingleTap={this.sendDeliveryCode}>
                                {sendCodeLabel}
                            </BasicButton>
                            <div className="signature-method-code-group">
                                <label className="signature-method-code-label">
                                    {T(
                                        'shippment_upload_signature_method_input_label_input_hint',
                                    )}
                                </label>
                                <input
                                    className="signature-method-code-input"
                                    disabled={interactionLocked}
                                    value={verificationCode}
                                    onChange={this.handleVerificationCodeChange}
                                    placeholder={T(
                                        'shippment_upload_signature_method_input_placeholder',
                                    )}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="signature-method-section">
                    <div className="signature-method-section-header">
                        <span className="signature-method-section-text">
                            {receiveMethod === RECEIVE_METHOD.DELEGATE
                                ? T('shippment_upload_designated_location')
                                : T('shippment_upload_sign_receipt')}
                        </span>
                    </div>
                    <BaseImageUploader
                        className="upload-method-upload"
                        testid="receipt-signature-file-input"
                        disabled={interactionLocked}
                        tag="button"
                        type="button"
                        isPreventDefault={false}
                        onClickOfSingleTap={this.handleChooseMainImage}>
                        {chooseImage.url ? (
                            <img src={chooseImage.url} alt="" />
                        ) : (
                            <>
                                <span className="upload-method-upload-icon" />
                                <span className="upload-method-upload-text">
                                    {receiveMethod === RECEIVE_METHOD.DELEGATE
                                        ? T(
                                              'shippment_upload_click_designated_location_by_photo',
                                          )
                                        : T(
                                              'shippment_upload_sign_receipt_by_photo_here',
                                          )}
                                </span>
                            </>
                        )}
                    </BaseImageUploader>
                </div>

                <div className="signature-method-section">
                    <div className="signature-method-section-header other-photo-upload-header">
                        <span className="signature-method-section-text">
                            {T('shippment_upload_other_photos')}
                        </span>
                    </div>
                    {this.renderOtherPhotoContent()}
                </div>

                <input
                    ref={this.mainImageInputRef}
                    className="other-photo-upload-input"
                    type="file"
                    accept="image/*"
                    onChange={this.handleSelectedMainImage}
                />
                <input
                    ref={this.otherImageInputRef}
                    className="other-photo-upload-input"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={this.handleSelectedOtherImages}
                />

                <div className="upload-method-actions">
                    <BasicButton
                        type="primary"
                        className="upload-method-submit"
                        testid="receipt-confirm-btn"
                        disabled={this.isSubmitButtonDisabled()}
                        loading={uploadFileBtnIsLoading}
                        onClickOfSingleTap={this.handleSubmit}>
                        <span>{T('order_confirm_receive')}</span>
                    </BasicButton>
                    <BasicButton
                        type="default"
                        className="upload-method-cancel"
                        disabled={interactionLocked}
                        onClickOfSingleTap={() => {
                            this.resetState();
                            onClose();
                        }}>
                        <span>{T('cancel')}</span>
                    </BasicButton>
                </div>
            </div>
        );
    }
}

ShipmentReceiveWithOtherPhotosDialog.defaultProps = {
    onLoadingChange: () => {},
    mode: 'receive',
    initialReceiveMethod: RECEIVE_METHOD.CUSTOMER,
    initialVerificationCode: '',
    initialMainImageUrl: '',
    initialMainImageKey: '',
    initialOtherImages: [],
    lockReceiveMethod: false,
};

const mapDispatchToProps = (dispatch) => ({
    openNotification: (message, type, options) =>
        dispatch(openNotification(message, type, options)),
    deleteShipmentAttachment: (params) =>
        dispatch(deleteShipmentAttachment(params)),
    getReceiveCode: (data) => dispatch(getReceiveCode(data)),
    uploadReceiveFile: (data) => dispatch(uploadReceiveImage(data)),
    receiveOrderRequest: (data) => dispatch(receiveOrder(data)),
    updateShipmentRequest: (data) => dispatch(updateShipment(data)),
});

export default connect(
    null,
    mapDispatchToProps,
)(ShipmentReceiveWithOtherPhotosDialog);
